import { useQuery } from '@tanstack/react-query';
import { listPlaces } from '@littlebooker/dal';
import { X } from 'lucide-react';

interface PlaceMultiSelectProps {
  value: string[];
  onChange: (placeIds: string[]) => void;
}

/**
 * Chip-based multiselect of places (stages/venues). Used to restrict a person to
 * specific locations. An empty selection means no restriction.
 */
export function PlaceMultiSelect({ value, onChange }: PlaceMultiSelectProps) {
  const { data: places = [], isLoading } = useQuery({
    queryKey: ['places', { is_active: true }],
    queryFn: () => listPlaces({ is_active: true }),
  });

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading places…</p>;
  if (places.length === 0)
    return <p className="text-sm text-muted-foreground">No places defined yet.</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {places.map((place) => {
        const selected = value.includes(place.id);
        return (
          <button
            key={place.id}
            type="button"
            onClick={() => toggle(place.id)}
            className={
              selected
                ? 'inline-flex items-center gap-1 rounded-full border border-primary bg-primary/15 px-3 py-1 text-sm text-primary'
                : 'inline-flex items-center gap-1 rounded-full border border-input px-3 py-1 text-sm text-muted-foreground hover:bg-accent'
            }
          >
            {place.name}
            {selected && <X className="h-3 w-3" />}
          </button>
        );
      })}
    </div>
  );
}
